import { useContext, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BedDouble, ChevronDown, CircleDot, MessageCircle, Plane, Train, Car, Tent } from "lucide-react";
import { EASE, Reveal, SectionHeading, WhatsAppButton, useWhatsApp } from "../components/Shared";
import Seo from "../components/Seo";
import EnquiryForm, { DEST_OPTIONS } from "../components/EnquiryForm";
import { LangContext } from "../i18n";
import { ACCOMMODATION, DESTINATIONS } from "../data/destinations";

const DEST_OPTION_INDEX = { prayagraj: 0, haridwar: 1, nashik: 2, ujjain: 3 };

export default function DestinationPage() {
  const { city } = useParams();
  const { lang, copy } = useContext(LangContext);
  const [faq, setFaq] = useState(null);
  const openWhatsApp = useWhatsApp();
  const data = DESTINATIONS[city];
  if (!data) return <Navigate to="/kumbh" replace />;
  const d = data[lang];
  const k = copy.kumbh;
  const scrollToEnquiry = () => {
    if (window.__lenis) window.__lenis.scrollTo("#enquiry", { offset: -72 });
    else document.querySelector("#enquiry")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="dest-page">
      <Seo title={d.seoTitle} description={d.seoDesc} path={`/kumbh/${city}`} />

      <section className="dest-hero section-pad">
        <div className="dest-hero-copy">
          <Reveal><Link to="/kumbh" className="back-link" data-testid="dest-back-link"><ArrowLeft size={15} /> Kumbh 2027–28</Link></Reveal>
          <motion.p className="eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>{d.tagline}</motion.p>
          <h1 className="dest-title" key={lang}>
            <span className="mask-line"><motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>{d.name}</motion.span></span>
          </h1>
          <Reveal delay={0.25}><p className="body-copy">{d.overview}</p></Reveal>
          <Reveal delay={0.35} className="hero-actions">
            <button className="button" onClick={scrollToEnquiry} data-testid="dest-enquire-button"><MessageCircle size={16} /> {copy.enquiry.title}</button>
            <Link to="/kumbh" className="button button-outline" data-testid="dest-stays-link"><Tent size={16} /> {k.ctaStay}</Link>
          </Reveal>
        </div>
        <Reveal className="dest-hero-visual" delay={0.2}>
          <img src={data.img} alt={d.heroAlt} data-testid="dest-hero-image" />
        </Reveal>
      </section>

      <section className="section-pad dest-sig">
        <SectionHeading data={{ kicker: copy.about.kicker, title: d.tagline }} />
        <Reveal><p className="body-copy">{d.significance}</p></Reveal>
      </section>

      <section className="section-pad dest-dates">
        <SectionHeading data={{ kicker: k.datesKicker, title: d.datesTitle }} />
        <Reveal>
          <div className="dates-table" data-testid="dest-dates-table">
            {d.dates.map(([label, value]) => (
              <div className="dates-row" key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal><p className="events-note">{k.datesNote}</p></Reveal>
        <Reveal delay={0.1}>
          <div className="camp-note" data-testid="dest-camp-note"><Tent size={18} /><p>{d.camp}</p></div>
        </Reveal>
      </section>

      <section className="section-pad dest-reach">
        <SectionHeading data={{ kicker: lang === "hi" ? "कैसे पहुँचें" : "Getting there", title: lang === "hi" ? "ट्रेन, वायु और सड़क मार्ग" : "By train, air and road" }} />
        <div className="reach-grid">
          {[[Train, d.reach.train, "train"], [Plane, d.reach.air, "air"], [Car, d.reach.road, "road"]].map(([Icon, text, key], i) => (
            <Reveal key={key} delay={i * 0.08}>
              <div className="reach-card" data-testid={`dest-reach-${key}`}>
                <Icon size={20} />
                <p>{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad dest-nearby">
        <SectionHeading data={{ kicker: lang === "hi" ? "आसपास के दर्शन" : "Nearby", title: lang === "hi" ? "पवित्र स्थल और दर्शन" : "Sacred places to visit" }} />
        <Reveal>
          <div className="chip-row" data-testid="dest-nearby-chips">
            {d.nearby.map((n) => <span className="chip" key={n}><CircleDot size={12} /> {n}</span>)}
          </div>
        </Reveal>
        <div className="tips-list">
          {d.tips.map((tip, i) => (
            <Reveal key={tip} delay={i * 0.07}>
              <div className="tip-item" data-testid={`dest-tip-${i}`}><span>{String(i + 1).padStart(2, "0")}</span><p>{tip}</p></div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad dest-stays">
        <SectionHeading data={{ kicker: k.accKicker, title: k.accTitle, sub: k.accSub }} />
        <div className="acc-grid compact">
          {ACCOMMODATION.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08} className="acc-card-wrap">
              <article className="acc-card" data-testid={`dest-acc-${a.id}`}>
                <div className="acc-photo"><img src={a.img} alt={a[lang].alt} loading="lazy" /></div>
                <div className="acc-body">
                  <h3>{a[lang].name}</h3>
                  <p className="acc-spec"><BedDouble size={15} /> {a[lang].beds}</p>
                  <div className="acc-price"><span className="acc-price-value">{k.onRequest}</span><span className="acc-price-unit">{k.perTent}</span></div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="faq-section section-pad">
        <SectionHeading data={{ kicker: k.faqKicker, title: k.faqTitle }} />
        <div className="faq-list">
          {d.faqs.map(([q, a], i) => (
            <Reveal key={q} delay={i * 0.05}>
              <div className={`faq-item ${faq === i ? "open" : ""}`}>
                <button onClick={() => setFaq(faq === i ? null : i)} data-testid={`dest-faq-${i}`}>
                  <span>{q}</span>
                  <motion.span animate={{ rotate: faq === i ? 180 : 0 }} transition={{ duration: 0.3 }}><ChevronDown size={18} /></motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {faq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} style={{ overflow: "hidden" }}>
                      <p data-testid={`dest-faq-answer-${i}`}>{a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="enquiry" className="section-pad enquiry-section">
        <div className="enquiry-wrap">
          <div className="enquiry-copy">
            <SectionHeading data={{ kicker: copy.enquiry.kicker, title: `${d.name} — ${copy.enquiry.title}`, sub: copy.enquiry.sub }} />
            <WhatsAppButton testid="dest-enquiry-whatsapp" onClick={() => openWhatsApp("kumbh")}>{copy.hero.primary}</WhatsAppButton>
          </div>
          <Reveal delay={0.1}>
            <EnquiryForm key={lang} defaultDestination={DEST_OPTIONS[lang][DEST_OPTION_INDEX[city]]} />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
